/* Hamak i Ja — collection filters, sorting, "Pokaż więcej" and the mobile filter drawer.
   Markup: sections/main-collection-product-grid.liquid (<hk-facets>), snippets/hk-collection-facets.liquid.
   Re-renders through the Section Rendering API (?section_id=…) and swaps the [data-hk-swap] regions;
   without JS the form has a submit button (noscript) and "Pokaż więcej" is a plain pagination link. */
(() => {
  if (customElements.get('hk-facets')) return;

  const debounce = (fn, ms) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...a), ms);
    };
  };
  const cache = new Map();
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const getHtml = async (url) => {
    if (cache.has(url)) return cache.get(url);
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    cache.set(url, html);
    return html;
  };
  const mqDesktop = window.matchMedia('(min-width: 990px)');

  class HkFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest('.hk-section') || this;
      this.lastQuery = window.location.search.replace(/^\?/, '');
      this.queueSubmit = debounce(() => this.submit(), 450);

      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.matches('[data-hk-sort]')) {
          this.submit();
        } else if (t.matches('input[type="checkbox"], input[type="range"]') && t.closest('[data-hk-facets-form]')) {
          this.queueSubmit();
        }
      });
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-hk-price] input[type="range"]')) this.syncPrice(e.target);
      });
      this.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-hk-facets-form]')) return;
        e.preventDefault();
        this.submit();
      });
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isDrawerOpen()) this.closeDrawer();
      });

      // quick chips in the collection banner (another section)
      this.onChip = (e) => {
        const chip = e.target.closest('[data-hk-chips] a[data-hk-chip]');
        if (!chip || e.metaKey || e.ctrlKey || e.shiftKey) return;
        const url = new URL(chip.href, window.location.href);
        if (url.pathname !== window.location.pathname) return;
        e.preventDefault();
        url.searchParams.delete('page');
        this.render(url.searchParams.toString());
      };
      document.addEventListener('click', this.onChip);

      this.onPop = () => {
        const q = window.location.search.replace(/^\?/, '');
        if (q === this.lastQuery) return;
        this.render(q, false);
      };
      window.addEventListener('popstate', this.onPop);

      this.onMq = () => {
        if (mqDesktop.matches && this.isDrawerOpen()) this.closeDrawer(false);
      };
      mqDesktop.addEventListener?.('change', this.onMq);

      this.querySelectorAll('[data-hk-price]').forEach((p) => this.paintPrice(p));
      this.initFavs(this);

      // sticky sidebar taller than the viewport: stick by its bottom edge instead of hiding the end of the list
      this.fitAside = () => {
        const aside = this.drawer;
        if (!aside) return;
        if (!mqDesktop.matches) {
          aside.style.top = '';
          return;
        }
        aside.style.top = `${Math.min(24, window.innerHeight - aside.offsetHeight - 24)}px`;
      };
      if (this.drawer && 'ResizeObserver' in window) {
        this.asideRo = new ResizeObserver(this.fitAside);
        this.asideRo.observe(this.drawer);
      }
      window.addEventListener('resize', this.fitAside, { passive: true });
      this.fitAside();
    }

    disconnectedCallback() {
      this.asideRo?.disconnect();
      window.removeEventListener('resize', this.fitAside);
      document.removeEventListener('click', this.onChip);
      window.removeEventListener('popstate', this.onPop);
      mqDesktop.removeEventListener?.('change', this.onMq);
      if (this.isDrawerOpen()) document.documentElement.classList.remove('hk-scroll-lock');
    }

    /* ---------- query building ---------- */
    get form() {
      return this.querySelector('[data-hk-facets-form]');
    }

    query() {
      const fd = this.form ? new FormData(this.form) : new FormData();
      const sort = this.querySelector('[data-hk-sort]');
      if (sort && !this.form) fd.set('sort_by', sort.value);
      this.querySelectorAll('[data-hk-price]').forEach((wrap) => {
        const max = Number(wrap.dataset.max) || 0;
        const lo = wrap.querySelector('[data-hk-price-min]');
        const hi = wrap.querySelector('[data-hk-price-max]');
        if (lo && Number(lo.value) <= 0) fd.delete(lo.name);
        if (hi && Number(hi.value) >= max) fd.delete(hi.name);
      });
      const sp = new URLSearchParams();
      fd.forEach((v, k) => {
        if (v !== '' && k !== 'page') sp.append(k, v);
      });
      return sp.toString();
    }

    submit() {
      this.render(this.query());
    }

    /* ---------- rendering ---------- */
    async render(query, push = true) {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}${query ? `&${query}` : ''}`;
      const deferred = this.startUpdateEvent(query);
      this.section.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const html = await getHtml(url);
        this.swap(parse(html));
        this.lastQuery = query;
        if (push) {
          const state = Object.assign({}, window.history.state || {}, { hkFacets: true });
          window.history.pushState(state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        }
        this.syncChips();
        this.announce();
        deferred?.resolve({ productsCount: this.count() });
      } catch (err) {
        console.error('[hk-facets]', err);
        deferred?.reject(err);
        window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
      } finally {
        this.section.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }

    swap(doc) {
      const closed = [...this.querySelectorAll('details.hk-facets__group:not([open])')].map((d) => d.id);
      const focusId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
      const scroller = this.querySelector('.hk-cgrid__aside-inner');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      this.querySelectorAll('[data-hk-swap]').forEach((el) => {
        const next = doc.querySelector(`[data-hk-swap="${el.dataset.hkSwap}"]`);
        if (next) el.replaceWith(document.adoptNode(next));
      });

      closed.forEach((id) => {
        const d = id && this.querySelector(`#${CSS.escape(id)}`);
        if (d) d.open = false;
      });
      if (scroller) scroller.scrollTop = scrollTop;
      if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
      if (this.isDrawerOpen()) this.setDrawerButtons(true);
      this.querySelectorAll('[data-hk-price]').forEach((p) => this.paintPrice(p));
      this.initFavs(this);
    }

    async loadMore(btn) {
      const href = btn.getAttribute('href');
      if (!href || btn.classList.contains('is-loading')) return;
      const label = btn.textContent;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      if (btn.dataset.loadingText) btn.textContent = btn.dataset.loadingText;
      try {
        const url = new URL(href, window.location.href);
        url.searchParams.set('section_id', this.sectionId);
        const doc = parse(await getHtml(url.toString()));
        const grid = this.querySelector('[data-hk-grid]');
        const items = [...doc.querySelectorAll('[data-hk-grid] > li')];
        items.forEach((li) => grid.appendChild(document.adoptNode(li)));
        const more = this.querySelector('[data-hk-swap="more"]');
        const nextMore = doc.querySelector('[data-hk-swap="more"]');
        if (more && nextMore) more.replaceWith(document.adoptNode(nextMore));
        this.initFavs(grid);
        items[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
      } catch (err) {
        console.error('[hk-facets]', err);
        window.location.href = href;
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      }
    }

    onClick(e) {
      const link = e.target.closest('a[data-hk-facets-link]');
      if (link && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        const url = new URL(link.href, window.location.href);
        url.searchParams.delete('page');
        this.render(url.searchParams.toString());
        return;
      }
      const more = e.target.closest('a[data-hk-load-more]');
      if (more && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        this.loadMore(more);
        return;
      }
      const opener = e.target.closest('[data-hk-drawer-open]');
      if (opener) {
        this.openDrawer(opener);
        return;
      }
      if (e.target.closest('[data-hk-drawer-close]')) this.closeDrawer();
    }

    /* ---------- price slider ---------- */
    syncPrice(input) {
      const wrap = input.closest('[data-hk-price]');
      const lo = wrap.querySelector('[data-hk-price-min]');
      const hi = wrap.querySelector('[data-hk-price-max]');
      if (Number(lo.value) > Number(hi.value)) {
        if (input === lo) lo.value = hi.value;
        else hi.value = lo.value;
      }
      this.paintPrice(wrap);
    }

    paintPrice(wrap) {
      const max = Number(wrap.dataset.max) || 1;
      const lo = wrap.querySelector('[data-hk-price-min]');
      const hi = wrap.querySelector('[data-hk-price-max]');
      const range = wrap.querySelector('.hk-facets__range');
      if (!lo || !hi || !range) return;
      range.style.setProperty('--hk-lo', String(Number(lo.value) / max));
      range.style.setProperty('--hk-hi', String(Number(hi.value) / max));
      // keep the lower handle reachable when both sit at the right end
      lo.style.zIndex = Number(lo.value) >= max - 1 ? '3' : '2';
      hi.style.zIndex = '2';
      const tpl = wrap.dataset.money || '1';
      const fmt = (v) => tpl.replace('1', Number(v).toLocaleString(document.documentElement.lang || 'pl'));
      const outMin = wrap.querySelector('[data-hk-price-out="min"]');
      const outMax = wrap.querySelector('[data-hk-price-out="max"]');
      if (outMin) outMin.textContent = fmt(lo.value);
      if (outMax) outMax.textContent = fmt(hi.value);
    }

    /* ---------- drawer (tablet / mobile) ---------- */
    get drawer() {
      return this.querySelector('[data-hk-drawer]');
    }

    isDrawerOpen() {
      return !!this.drawer?.classList.contains('is-open');
    }

    setDrawerButtons(open) {
      this.querySelectorAll('[data-hk-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', open ? 'true' : 'false'));
    }

    openDrawer(opener) {
      const d = this.drawer;
      if (!d || mqDesktop.matches) return;
      this.lastFocus = opener;
      d.classList.add('is-open');
      this.section.classList.add('is-drawer-open');
      d.setAttribute('role', 'dialog');
      d.setAttribute('aria-modal', 'true');
      const ov = this.querySelector('.hk-cgrid__overlay');
      if (ov) ov.hidden = false;
      document.documentElement.classList.add('hk-scroll-lock');
      document.documentElement.style.overflow = 'hidden';
      this.setDrawerButtons(true);
      window.setTimeout(() => d.querySelector('.hk-facets__close')?.focus(), 60);
    }

    closeDrawer(restoreFocus = true) {
      const d = this.drawer;
      if (!d) return;
      d.classList.remove('is-open');
      this.section.classList.remove('is-drawer-open');
      d.removeAttribute('role');
      d.removeAttribute('aria-modal');
      const ov = this.querySelector('.hk-cgrid__overlay');
      if (ov) ov.hidden = true;
      document.documentElement.classList.remove('hk-scroll-lock');
      document.documentElement.style.overflow = '';
      this.setDrawerButtons(false);
      if (restoreFocus) this.querySelector('[data-hk-drawer-open]')?.focus({ preventScroll: true });
    }

    /* ---------- helpers ---------- */
    count() {
      return Number(this.querySelector('[data-hk-count]')?.dataset.hkCount) || 0;
    }

    announce() {
      const live = this.querySelector('[data-hk-live]');
      const count = this.querySelector('[data-hk-count]');
      if (live && count) live.textContent = count.textContent.trim();
    }

    syncChips() {
      const params = new URLSearchParams(window.location.search);
      const hasFilter = [...params.keys()].some((k) => k.startsWith('filter.'));
      document.querySelectorAll('[data-hk-chips] a[data-hk-chip]:not([data-hk-chip-static])').forEach((chip) => {
        const val = chip.dataset.hkChip;
        let on;
        if (val === 'all') on = !hasFilter;
        else {
          const i = val.indexOf('=');
          const k = val.slice(0, i);
          const v = val.slice(i + 1).toLowerCase();
          on = params.getAll(k).some((x) => x.toLowerCase() === v);
        }
        chip.classList.toggle('is-active', on);
        if (on) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });
    }

    initFavs(root) {
      let favs = [];
      try {
        favs = JSON.parse(localStorage.getItem('hk-favs') || '[]');
      } catch (_) {
        favs = [];
      }
      root.querySelectorAll('[data-hk-fav]').forEach((b) => b.setAttribute('aria-pressed', favs.includes(b.dataset.hkFav) ? 'true' : 'false'));
    }

    startUpdateEvent(query) {
      const { CollectionUpdateEvent } = window.StandardEvents || {};
      if (!CollectionUpdateEvent || typeof CollectionUpdateEvent.createPromise !== 'function') return null;
      try {
        const params = new URLSearchParams(query);
        const deferred = CollectionUpdateEvent.createPromise();
        const comp = this.closest('collection-component');
        this.dispatchEvent(
          new CollectionUpdateEvent({
            collection: { id: comp?.dataset.collectionId || null, handle: this.dataset.collectionHandle || '', productsCount: this.count() },
            productFilters: CollectionUpdateEvent.parseProductFilters(params),
            sortKey: CollectionUpdateEvent.getSortKey(params),
            promise: deferred.promise,
          }),
        );
        return deferred;
      } catch (_) {
        return null;
      }
    }
  }

  customElements.define('hk-facets', HkFacets);

  // help card "Tabela rozmiarów" → size comparison popup (<dialog>); click on the dark backdrop closes it
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-hk-sizes-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        e.preventDefault();
        dialog.showModal();
      }
      return;
    }
    if (e.target.matches && e.target.matches('dialog.hk-szm[open]')) e.target.close();
  });
})();